import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Radio,
  Check,
  ArrowRight,
  Loader2,
  X
} from 'lucide-react'
import { clsx } from 'clsx'

// 信号类型定义
interface Signal {
  id: string
  strength: 'Strong' | 'Medium' | 'Weak'
  description: string
  observed: boolean
  observedAt?: string
  // 关联信息
  actionId?: string
  actionContent?: string
  hypothesisId?: string
  hypothesisCategory?: string
  hypothesisName?: string  // 中文名称
}

interface OverviewSignalTrackerProps {
  signals: Signal[]
  onObserve?: (signalId: string) => Promise<void>
  onUnobserve?: (signalId: string) => Promise<void>  // TASK-H8: 取消观测回调
  onViewDetails?: () => void
  isLoading?: boolean
}

// 信号强度配置
const STRENGTH_CONFIG = {
  Strong: {
    label: '强',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
  },
  Medium: {
    label: '中',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-200',
  },
  Weak: {
    label: '弱',
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-200',
  },
}

export default function OverviewSignalTracker({
  signals,
  onObserve,
  onUnobserve,
  onViewDetails,
  isLoading = false,
}: OverviewSignalTrackerProps) {
  // TASK-H8: 取消确认状态
  const [confirmingUnobserve, setConfirmingUnobserve] = useState<string | null>(null)
  const [unobservingId, setUnobservingId] = useState<string | null>(null)

  const handleUnobserve = async (signalId: string) => {
    if (!onUnobserve) return
    setUnobservingId(signalId)
    try {
      await onUnobserve(signalId)
    } finally {
      setUnobservingId(null)
      setConfirmingUnobserve(null)
    }
  }
  // 统计
  const totalCount = signals.length
  const observedCount = signals.filter(s => s.observed).length
  const progress = totalCount > 0 ? (observedCount / totalCount) * 100 : 0

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  if (signals.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="text-center py-6 text-gray-400">
          <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">暂无预期信号</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* 头部 */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">📡</span>
            <h3 className="font-semibold text-gray-900">信号追踪</h3>
          </div>
          <span className="text-xs text-gray-500">
            {observedCount}/{totalCount} 已观测
          </span>
          {/* 进度条 */}
          <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                progress >= 80 ? 'bg-green-500' :
                progress >= 40 ? 'bg-amber-500' :
                'bg-gray-300'
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            详情
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 信号列表（紧凑模式） */}
      <div className="p-4 space-y-2">
        {signals.slice(0, 5).map((signal, index) => {
          const config = STRENGTH_CONFIG[signal.strength] || STRENGTH_CONFIG.Weak
          
          return (
            <motion.div
              key={signal.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={clsx(
                'flex items-center gap-3 p-3 rounded-lg border transition-colors group',
                signal.observed
                  ? 'bg-green-50/50 border-green-200'
                  : 'bg-white border-gray-100 hover:border-gray-200'
              )}
            >
              {/* 状态图标 */}
              <div className={clsx(
                'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0',
                signal.observed
                  ? 'bg-green-500 text-white'
                  : config.bgColor
              )}>
                {signal.observed ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <Radio className={clsx('w-3 h-3', config.color)} />
                )}
              </div>

              {/* 信号强度标签 */}
              <span className={clsx(
                'text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0',
                config.bgColor, config.color
              )}>
                {config.label}
              </span>

              {/* 描述和关联信息 */}
              <div className="flex-1 min-w-0">
                <span className={clsx(
                  'text-sm block truncate',
                  signal.observed ? 'text-green-700' : 'text-gray-700'
                )}>
                  {signal.description}
                </span>
                {/* 关联的假设和动作 */}
                {(signal.hypothesisName || signal.actionContent) && (
                  <span className="text-xs text-gray-400 block truncate mt-0.5">
                    └ 来自: {signal.hypothesisName || signal.hypothesisCategory}
                    {signal.actionContent && ` → ${signal.actionContent.slice(0, 30)}${signal.actionContent.length > 30 ? '...' : ''}`}
                  </span>
                )}
              </div>

              {/* 操作按钮 */}
              {!signal.observed && onObserve && (
                <button
                  onClick={() => onObserve(signal.id)}
                  className="text-xs px-2 py-1 text-green-600 hover:bg-green-50 rounded transition-colors flex-shrink-0"
                >
                  标记
                </button>
              )}
              {/* TASK-H8: 已观测信号 - 显示取消按钮 */}
              {signal.observed && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {confirmingUnobserve === signal.id ? (
                    // 确认取消状态
                    <div className="flex items-center gap-1">
                      {unobservingId === signal.id ? (
                        <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <button
                            onClick={() => handleUnobserve(signal.id)}
                            className="text-xs px-1.5 py-0.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="确认取消"
                          >
                            确认
                          </button>
                          <button
                            onClick={() => setConfirmingUnobserve(null)}
                            className="text-xs px-1 py-0.5 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    // 正常状态
                    <>
                      <span className="text-xs text-green-600">✓</span>
                      {onUnobserve && (
                        <button
                          onClick={() => setConfirmingUnobserve(signal.id)}
                          className="text-xs text-gray-400 hover:text-gray-600 ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="取消观测（不会回滚假设状态）"
                        >
                          取消
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </motion.div>
          )
        })}

        {/* 更多信号提示 */}
        {signals.length > 5 && (
          <button
            onClick={onViewDetails}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            还有 {signals.length - 5} 个信号，点击查看全部
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * 信号追踪骨架屏
 */
export function OverviewSignalTrackerSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-5 bg-gray-200 rounded w-24" />
          <div className="h-4 bg-gray-200 rounded w-16" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-12" />
      </div>
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-12 bg-gray-100 rounded-lg" />
        ))}
      </div>
    </div>
  )
}
